// Iter 31.139 (30 May 2026) — Global credit-wall sheet.
//
// Listens on `window` for `shed:credits-required` (dispatched by the
// axios 402 interceptor in `lib/api.js`). Opens a dossier-style sheet
// with the three top-up tiers (£5/£10/£20). One tap on a tier creates
// a Stripe checkout session via `POST /api/credits/topup-checkout` and
// redirects the operator to Stripe Hosted Checkout.
//
// Canon — keep the operational tone. No marketing hype. Headline reads
// the ACTION the operator was trying to do, the current balance, and
// the required amount. Footer line is verbatim Rule 15: "Agents
// should generate revenue for One."
//
// Mounted once inside <RadioProvider> in App.js so every authenticated
// surface inherits it without per-page wiring.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ACTION_LABELS = {
    brief: "Brief generation",
    cv_compose: "CV composer",
    cold_read: "Cold read",
    lyric_video: "Lyric video (Mureka + ffmpeg)",
    lyric_video_sora: "Lyric video (Sora 2)",
};

const TIERS = [
    { id: "5",  gbp: "£5",  credits: 50,  blurb: "Light cover. ~50 briefs." },
    { id: "10", gbp: "£10", credits: 110, blurb: "+10% bonus. Workhorse." },
    { id: "20", gbp: "£20", credits: 240, blurb: "+20% bonus. Best value." },
];

export default function CreditWallSheet() {
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState("");
    const [err, setErr] = useState("");
    const [ctx, setCtx] = useState(null);  // iter 31.140 — cross-sell context

    useEffect(() => {
        const h = (e) => {
            setDetail(e.detail || null);
            setErr("");
            setBusy("");
            setCtx(null);
            setOpen(true);
            // Iter 31.140 — fetch the wall-context (tier + cross-sell)
            // when the sheet opens. Failure is silent — cross-sell line
            // just won't render.
            api.get("/credits/wall-context")
                .then((r) => setCtx(r.data || null))
                .catch(() => setCtx(null));
        };
        window.addEventListener("shed:credits-required", h);
        return () => window.removeEventListener("shed:credits-required", h);
    }, []);

    const topup = async (tier) => {
        setBusy(`topup-${tier}`); setErr("");
        try {
            const { data } = await api.post("/credits/topup-checkout", { tier });
            if (data?.url) {
                window.location.assign(data.url);
                return;
            }
            setErr("No checkout URL returned.");
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${
                typeof e?.response?.data?.detail === "string"
                    ? e.response.data.detail
                    : (e?.response?.data?.detail?.message || e.message)
            }`);
        } finally { setBusy(""); }
    };

    // Iter 31.140 — Subscribe-instead cross-sell. Posts to /api/billing/checkout
    // with the recommended next tier (operator or cooperative) and redirects
    // to Stripe Hosted Checkout with a 7-day free trial.
    const subscribe = async (tier) => {
        setBusy(`sub-${tier}`); setErr("");
        try {
            const { data } = await api.post("/billing/checkout", { tier });
            if (data?.url) {
                window.location.assign(data.url);
                return;
            }
            setErr("No checkout URL returned.");
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${
                typeof e?.response?.data?.detail === "string"
                    ? e.response.data.detail
                    : (e?.response?.data?.detail?.message || e.message)
            }`);
        } finally { setBusy(""); }
    };

    const action = detail?.action || "operation";
    const balance = Number(detail?.balance ?? 0);
    const required = Number(detail?.required ?? 0);
    const shortfall = Math.max(0, required - balance);
    const actionLabel = ACTION_LABELS[action] || action;

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
                data-testid="credit-wall-sheet"
                className="max-w-lg p-0 border-0 bg-transparent shadow-none"
            >
                <div
                    className="dossier-paper relative bg-[rgb(var(--c-paper))] text-[rgb(var(--c-ink))] p-7 font-mono shadow-2xl border border-[rgb(var(--c-paper-fold))]"
                    style={{ fontFamily: "'Courier Prime', 'Courier New', monospace" }}
                >
                    {/* CLASSIFIED stamp */}
                    <div
                        data-testid="credit-wall-stamp"
                        className="absolute top-3 right-3 px-3 py-1 text-xs tracking-[0.25em] border-2 rotate-[6deg] opacity-90"
                        style={{
                            color: "var(--stamp-red)",
                            borderColor: "var(--stamp-red)",
                            fontWeight: 700,
                        }}
                    >
                        INSUFFICIENT FUNDS
                    </div>

                    <div className="text-[10px] tracking-[0.3em] opacity-60 mb-2">
                        FILE · REF BJLIAM / CREDITS · 402
                    </div>

                    <DialogTitle
                        data-testid="credit-wall-title"
                        className="text-2xl font-bold tracking-wide mb-1"
                    >
                        WALL.
                    </DialogTitle>
                    <DialogDescription className="text-sm leading-relaxed opacity-90 mb-4">
                        <span data-testid="credit-wall-action">{actionLabel}</span> requires{" "}
                        <span data-testid="credit-wall-required" className="font-bold">{required}</span> credits.
                        You have <span data-testid="credit-wall-balance" className="font-bold">{balance}</span>.
                        Short by <span className="font-bold">{shortfall}</span>.
                    </DialogDescription>

                    <div className="border-t border-dashed border-[rgb(var(--c-paper-fold))] my-4" />

                    <div className="text-[10px] tracking-[0.25em] opacity-60 mb-3">
                        // TOP-UP TIERS · ONE-OFF · NO SUBSCRIPTION
                    </div>

                    <div className="space-y-2.5">
                        {TIERS.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`credit-wall-topup-${t.id}`}
                                disabled={!!busy}
                                onClick={() => topup(t.id)}
                                className="w-full text-left flex items-center justify-between gap-3 px-4 py-3 border border-[rgb(var(--c-paper-fold))] bg-[rgb(var(--c-paper-light))] hover:bg-[rgb(var(--c-paper-dark))] transition-colors disabled:opacity-50"
                            >
                                <div>
                                    <div className="text-base font-bold tracking-wide">
                                        {t.gbp} <span className="opacity-50">·</span> {t.credits} credits
                                    </div>
                                    <div className="text-[11px] opacity-70 mt-0.5">{t.blurb}</div>
                                </div>
                                <div className="text-xs tracking-[0.2em] opacity-60">
                                    {busy === `topup-${t.id}` ? "OPENING…" : "SIGN →"}
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Iter 31.140 — Subscribe-instead cross-sell.
                        Renders only when the operator isn't already at
                        the top of the ladder (i.e., not Cooperative).
                        Stronger nudge copy shows when they've already
                        topped up this month — pure server-side decision. */}
                    {ctx?.cross_sell && (
                        <button
                            data-testid="credit-wall-cross-sell"
                            disabled={!!busy}
                            onClick={() => subscribe(ctx.cross_sell.tier)}
                            className="w-full text-left mt-3 px-4 py-3 border border-dashed border-[rgb(var(--c-stamp-blue))] bg-[rgb(var(--c-paper-light))] hover:bg-[rgb(var(--c-paper))] transition-colors disabled:opacity-50"
                        >
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-[10px] tracking-[0.25em] opacity-60 mb-0.5">
                                        // OR · SUBSCRIBE INSTEAD
                                    </div>
                                    <div
                                        data-testid="credit-wall-cross-sell-label"
                                        className="text-sm font-bold tracking-wide"
                                    >
                                        {ctx.cross_sell.short}
                                    </div>
                                    {ctx.nudge && (
                                        <div
                                            data-testid="credit-wall-cross-sell-nudge"
                                            className="text-[11px] opacity-70 mt-0.5 italic"
                                        >
                                            {ctx.nudge}
                                        </div>
                                    )}
                                </div>
                                <div className="text-xs tracking-[0.2em] opacity-60 whitespace-nowrap">
                                    {busy === `sub-${ctx.cross_sell.tier}` ? "OPENING…" : "TRIAL →"}
                                </div>
                            </div>
                        </button>
                    )}

                    {err && (
                        <div
                            data-testid="credit-wall-error"
                            className="mt-4 text-xs text-[rgb(var(--c-stamp-red))] border border-[rgb(var(--c-stamp-red))] px-3 py-2"
                        >
                            {err}
                        </div>
                    )}

                    <div className="border-t border-dashed border-[rgb(var(--c-paper-fold))] mt-5 pt-3 text-[10px] tracking-wider opacity-60 italic">
                        Rule 15 — Agents should generate revenue for One.
                    </div>

                    <div className="flex items-center justify-between mt-3">
                        <a
                            data-testid="credit-wall-billing-link"
                            href="/billing"
                            className="text-[11px] tracking-[0.15em] underline opacity-70 hover:opacity-100"
                        >
                            See full ledger & subscriptions →
                        </a>
                        <button
                            data-testid="credit-wall-dismiss"
                            onClick={() => setOpen(false)}
                            className="text-[11px] tracking-[0.15em] opacity-50 hover:opacity-90"
                        >
                            Stand down
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
